import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { AuditType, RequestAuditLog } from '@/schema'
import { getEndOfDayUTC, getStartOfDayUTC } from '@/utils/common'
import { TABLE_PAGE_SIZE } from '@/utils/constants'

import {
  DOCUMENT_VIEW_AUDIT_QUERY_KEY,
  ENTITY_HISTORY_QUERY_KEY,
  HISTORY_DEFAULT_SORT,
  HISTORY_LIST_QUERY_KEY,
  HISTORY_LIST_STALE_TIME,
} from './constants'
import { auditLogApi } from './services'
import { HistoryFilters, HistoryTimeFrame } from './types'

export const useDocumentViewAudit = (
  documentId: string,
  documentVersionId: string
) => {
  return useQuery({
    queryKey: DOCUMENT_VIEW_AUDIT_QUERY_KEY(documentId, documentVersionId),
    queryFn: () => {
      return auditLogApi.getAuditLogList({
        audit_type: AuditType.DOCUMENT_VIEWED,
        limit: TABLE_PAGE_SIZE,
        offset: 0,
        filter_by: {
          document_id: [documentId],
          document_version_id: [documentVersionId],
        },
      })
    },
    enabled: !!documentId && !!documentVersionId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  })
}

export enum EntityHistoryType {
  COMPANY = 'company',
  PROJECT = 'project',
  DELIVERABLE_SET = 'deliverable_set',
  DOCUMENT = 'document',
}

/**
 * Fetches paginated audit history for any supported entity type.
 * Follows the same pattern as useCompanyInfinite — pageParam is a page number,
 * offset is derived as pageParam * TABLE_PAGE_SIZE.
 * Set `enabled=false` to defer fetching (e.g. until the drawer opens).
 */
export const useEntityHistory = (
  entity: EntityHistoryType | string,
  entityId: string,
  enabled = true,
  additionalFilters?: Record<string, unknown>
) => {
  return useInfiniteQuery({
    queryKey: ENTITY_HISTORY_QUERY_KEY(entity, entityId, additionalFilters),
    queryFn: ({ pageParam = 0 }) => {
      const defaultFilter: Record<string, unknown> = {
        [`${entity}_id`]: [entityId],
      }
      if (entity === EntityHistoryType.DELIVERABLE_SET) {
        defaultFilter.log_type = ['DELIVERABLE_SET']
      }
      return auditLogApi.getEntityHistoryList({
        audit_type: AuditType.HISTORY_LOG,
        limit: TABLE_PAGE_SIZE,
        offset: pageParam,
        sort_by: HISTORY_DEFAULT_SORT,
        filter_by: additionalFilters
          ? { ...defaultFilter, ...additionalFilters }
          : defaultFilter,
      })
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage.pagination?.has_next) {
        return (
          lastPageParam + (lastPage.pagination.page_size ?? TABLE_PAGE_SIZE)
        )
      }
      return undefined
    },
    enabled: !!entityId && enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    staleTime: 0,
    refetchInterval: 0,
    gcTime: 0,
  })
}

/**
 * Fetches the global audit history list for the History page via Infinite Scroll.
 * POSTs to /admin/v1/audits/list with audit_type=HISTORY_LOG.
 * Supports filtering by log_type, user_id, and created_at array.
 */
export const getCreatedAtFilter = (
  timeFrame?: HistoryTimeFrame | null,
  startDate?: string | null,
  endDate?: string | null
): string[] | undefined => {
  if (!timeFrame) return undefined

  if (timeFrame === 'CUSTOM') {
    const end = endDate ? getEndOfDayUTC(endDate) : getEndOfDayUTC(dayjs())
    if (startDate) return [getStartOfDayUTC(startDate), end]
    if (endDate) return [getStartOfDayUTC('2000-01-01'), end]
    return undefined
  }

  const now = dayjs()
  let start = getStartOfDayUTC(now)
  let end = getEndOfDayUTC(now)

  if (timeFrame === 'THIS_WEEK') {
    start = getStartOfDayUTC(now.startOf('isoWeek'))
    end = getEndOfDayUTC(now.endOf('isoWeek'))
  } else if (timeFrame === 'THIS_MONTH') {
    start = getStartOfDayUTC(now.startOf('month'))
    end = getEndOfDayUTC(now.endOf('month'))
  } else if (timeFrame === 'THIS_QUARTER') {
    const quarter = Math.floor(now.month() / 3)
    const quarterStart = now.month(quarter * 3).startOf('month')
    start = getStartOfDayUTC(quarterStart)
    end = getEndOfDayUTC(quarterStart.add(2, 'month').endOf('month'))
  }

  return [start, end]
}

export const useHistoryPageList = (filters: HistoryFilters) => {
  const limit = filters.limit ?? TABLE_PAGE_SIZE

  return useInfiniteQuery({
    queryKey: HISTORY_LIST_QUERY_KEY([
      limit,
      filters.user_id ?? null,
      filters.timeFrame ?? null,
      filters.startDate ?? null,
      filters.endDate ?? null,
      filters.logTypes ?? [],
    ]),
    queryFn: ({ pageParam = 0 }) => {
      const filterBy: Record<string, unknown> = {}

      if (filters.logTypes && filters.logTypes.length > 0) {
        filterBy.log_type = filters.logTypes
      }
      if (filters.user_id && filters.user_id.length > 0) {
        filterBy.user_id = filters.user_id
      }

      const createdAtFilter = getCreatedAtFilter(
        filters.timeFrame,
        filters.startDate,
        filters.endDate
      )
      if (createdAtFilter) {
        filterBy.created_at = createdAtFilter
      }

      return auditLogApi.getEntityHistoryList({
        audit_type: AuditType.HISTORY_LOG,
        limit,
        offset: pageParam,
        sort_by: HISTORY_DEFAULT_SORT,
        filter_by: Object.keys(filterBy).length > 0 ? filterBy : undefined,
      })
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage.pagination?.has_next) {
        return lastPageParam + (lastPage.pagination.page_size ?? limit)
      }
      return undefined
    },
    staleTime: HISTORY_LIST_STALE_TIME,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  })
}

export const useExportHistory = () => {
  return useMutation({
    mutationFn: (data: RequestAuditLog) => auditLogApi.exportHistory(data),
  })
}
